import VaultiScanSidebar from '../../components/sidebar/VaultiScanSidebar';
import VaultilakeSidebar from '../../components/sidebar/VaultilakeSidebar';
import {
  React,
  Outlet,
  useLocation,
  CustomSidebar,
  CustomerSidebar,
  CustomTopbar,
  SettingsSidebar,
  useState,
} from '../../utils/globalImports';
import styles from './Layout.module.css';

import VaultilakeScopeSelectorPopup from '../lakehouse/VaultilakeScopeSelectorPopup';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { ExtendedAccountResponse } from '../../store/types';
import { useNotificationStream } from '../../hooks/useNotificationStream';
import NotificationPanel from '../../components/Notifications/NotificationPanel';

const Layout: React.FC = () => {
  const location = useLocation();
  const [isSelectorOpen, setSelectorOpen] = React.useState(false);
  const { selectedCustomerId, selectedClientId } = useSelector((state: RootState) => state.vaultilake);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isVaultiscanCollapsed, setIsVaultiscanCollapsed] = useState(false);

  const auth = useSelector((state: RootState) => state.auth);
  const account = auth.account as ExtendedAccountResponse | null;
  const role = account?.user?.role || account?.role;

  // Initialize Notification Stream
  useNotificationStream();

  const toggleCollapse = () => {
    setIsCollapsed((prev) => !prev);
  };
  const toggleVaultiscanCollapse = () => {
    setIsVaultiscanCollapsed((prev) => !prev);
  };
  React.useEffect(() => {
    const shouldShowPopup =
      (role === 'SuperAdmin' || role === 'CustomerAdmin') &&
      (!selectedCustomerId || !selectedClientId) &&
      ['/data-platform', '/lakehouses'].some((path) => location.pathname.startsWith(path));

    setSelectorOpen(shouldShowPopup);
  }, [location.pathname, role, selectedCustomerId, selectedClientId]);

  const showCustomerSidebar = [
    '/category-management',
    '/user-management',
    '/customer-management',
    '/client-management',
    '/group-management',
    '/sdk',
  ].some((path) => location.pathname.startsWith(path));

  const showSettingsSidebar = [
    '/configurations',
    '/translations',
    '/llm-configuration',
    '/token-management',
    '/notifications/templates',
    '/custom-fields',
    '/agent-state',
  ].some((path) => location.pathname.startsWith(path));

  const showVaultiscanSidebar = [
    '/chat',
    '/document-dashboard',
    '/document-upload',
    '/agents',
    '/connectors',
    '/vaulticrawl',
  ].some((path) => location.pathname.startsWith(path));

  const showVaultilakeSidebar = [
    '/data-platform',
    '/lakehouses',
    '/data-agents',
    '/vaultilake-chat',
    '/knowledge-agent-management',
  ].some((path) => location.pathname.startsWith(path));

  const isOnChatPage = location.pathname.startsWith('/chat');
  const isNewChatPage = location.pathname === '/chat';
  React.useEffect(() => {
    if (isCollapsed) {
      document.body.classList.add('customer-sidebar-collapsed');
    } else {
      document.body.classList.remove('customer-sidebar-collapsed');
    }
    return () => {
      document.body.classList.remove('customer-sidebar-collapsed');
    };
  }, [isCollapsed]);

  React.useEffect(() => {
    if (isVaultiscanCollapsed) {
      document.body.classList.add('customer-sidebar-collapsed');
      document.body.classList.add('vaultiscan-sidebar-collapsed');
    } else {
      document.body.classList.remove('customer-sidebar-collapsed');
      document.body.classList.remove('vaultiscan-sidebar-collapsed');
    }

    if (showVaultiscanSidebar) {
      document.body.classList.add('vaultiscan-sidebar-open');
    } else {
      document.body.classList.remove('vaultiscan-sidebar-open');
    }

    return () => {
      document.body.classList.remove(
        'customer-sidebar-collapsed',
        'vaultiscan-sidebar-open',
        'vaultiscan-sidebar-collapsed',
      );
    };
  }, [isVaultiscanCollapsed, showVaultiscanSidebar]);

  return (
    <div className={styles['layout-container']}>
      <CustomTopbar isVaultiscanCollapsed={isVaultiscanCollapsed} />
      <div className={styles['sidebars-container']}>
        <CustomSidebar />

        {showCustomerSidebar && (
          <CustomerSidebar visible={showCustomerSidebar} collapsed={isCollapsed} onCollapseToggle={toggleCollapse} />
        )}

        {showSettingsSidebar && (
          <SettingsSidebar visible={showSettingsSidebar} collapsed={isCollapsed} onCollapseToggle={toggleCollapse} />
        )}

        {showVaultiscanSidebar && (
          <VaultiScanSidebar
            visible={showVaultiscanSidebar}
            collapsed={isVaultiscanCollapsed}
            onCollapseToggle={toggleVaultiscanCollapse}
          />
        )}

        {showVaultilakeSidebar && (
          <VaultilakeSidebar
            visible={showVaultilakeSidebar}
            collapsed={isCollapsed}
            onCollapseToggle={toggleCollapse}
          />
        )}
      </div>

      <div
        className={`
          ${styles['layout-main-content']} 
          ${(() => {
            if (!showCustomerSidebar && !showSettingsSidebar && !showVaultiscanSidebar && !showVaultilakeSidebar)
              return '';
            // On the chat page, VaultiScanSidebar + ChatSidebar both occupy the secondary area
            if (showVaultiscanSidebar && isOnChatPage) return styles['triple-sidebars-visible'];
            return styles['both-sidebars-visible'];
          })()}
        `}
        style={{
          overflow: isNewChatPage ? 'hidden' : 'auto',
          transition: 'margin-left 0.3s',
        }}
      >
        <Outlet context={{ isCollapsed, toggleCollapse, isVaultiscanCollapsed }} />
      </div>

      <VaultilakeScopeSelectorPopup open={isSelectorOpen} onClose={() => setSelectorOpen(false)} />
      <NotificationPanel />
    </div>
  );
};

export default Layout;
